// Enemy projectiles: the bullet ballet. Sent once as a spawn event and flown by
// every machine with this exact code, so a dodge you saw is a dodge that counted.

import { DT, TICK_RATE, segPointDist2 } from './core';
import { World, RayHit } from './world';

export const enum BKind { Pellet = 0, Slug = 1, Bomb = 2, Boulder = 3, IceNade = 4, MagNade = 5, GasCan = 6 }
/** Lobbed kinds that burst where they land (they never test against cowboys in flight). */
export const isLob = (k: number) => BULLETS[k].explode > 0;

export interface BulletDef {
  radius: number;
  speed: number;
  gravity: number;
  damage: number;
  explode: number; // AoE radius, 0 = none
  life: number; // seconds
}
export const BULLETS: BulletDef[] = [
  { radius: 0.2, speed: 12.5, gravity: 0, damage: 8, explode: 0, life: 5 },
  { radius: 0.12, speed: 62, gravity: 0, damage: 26, explode: 0, life: 2.5 },
  { radius: 0.32, speed: 0, gravity: 16, damage: 22, explode: 3.6, life: 5 },
  { radius: 0.6, speed: 0, gravity: 20, damage: 30, explode: 5, life: 5 },
  // Disruptor grenades (disruptor.gd): token 8 HE chip in a 4 m blast; the ice / magnetic proc is the threat
  { radius: 0.2, speed: 0, gravity: 16, damage: 8, explode: 4, life: 5 },
  { radius: 0.2, speed: 0, gravity: 16, damage: 8, explode: 4, life: 5 },
  // Engineer gas canister: no blast, it opens a gas cloud where it lands (explode = cloud radius)
  { radius: 0.2, speed: 0, gravity: 16, damage: 0, explode: 4, life: 5 },
];

/** What hurt a cowboy, for the nemesis rules: robot type and whether it's the outlaw. */
export interface HitSrc { type: number; nemesis: number }

export interface Bullet {
  id: number;
  kind: BKind;
  tick0: number;
  ox: number; oy: number; oz: number;
  vx: number; vy: number; vz: number;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  owner: number;
  /** host-side: who fired it, kept past the shooter's death (nemesis grudges and damage) */
  src?: HitSrc | null;
  dead: boolean;
}

export function makeBullet(id: number, kind: BKind, tick0: number, ox: number, oy: number, oz: number, vx: number, vy: number, vz: number, owner: number): Bullet {
  return { id, kind, tick0, ox, oy, oz, vx, vy, vz, x: ox, y: oy, z: oz, px: ox, py: oy, pz: oz, owner, dead: false };
}

/** Analytic position at a (possibly fractional) tick. */
export function bulletPosAt(b: Bullet, tick: number, out: { x: number; y: number; z: number }) {
  const t = Math.max(0, (tick - b.tick0) / TICK_RATE);
  const g = BULLETS[b.kind].gravity;
  out.x = b.ox + b.vx * t;
  out.y = b.oy + b.vy * t - 0.5 * g * t * t;
  out.z = b.oz + b.vz * t;
  return out;
}

const scratch: RayHit = { t: 0, kind: 0, nx: 0, ny: 0, nz: 0 };
const tmp = { x: 0, y: 0, z: 0 };

/**
 * Advance a bullet to `tick`. Returns 1 if it struck the world this step (b.x/y/z is the
 * impact point), 2 if it expired, 0 otherwise.
 */
export function advanceBullet(b: Bullet, tick: number, world: World): number {
  b.px = b.x; b.py = b.y; b.pz = b.z;
  bulletPosAt(b, tick, tmp);
  const def = BULLETS[b.kind];
  const dx = tmp.x - b.px, dy = tmp.y - b.py, dz = tmp.z - b.pz;
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
  if (d > 1e-5) {
    const hit = world.raycast(b.px, b.py, b.pz, dx / d, dy / d, dz / d, d, scratch);
    if (hit.kind !== 0) {
      b.x = b.px + (dx / d) * hit.t; b.y = b.py + (dy / d) * hit.t; b.z = b.pz + (dz / d) * hit.t;
      return 1;
    }
  }
  b.x = tmp.x; b.y = tmp.y; b.z = tmp.z;
  if ((tick - b.tick0) * DT > def.life || b.y < -80) return 2;
  return 0;
}

/** Did this step's segment pass through a standing cowboy's capsule at (x,y,z)? */
export function bulletHitsCapsule(b: Bullet, x: number, y: number, z: number, r: number) {
  const rr = r + BULLETS[b.kind].radius;
  // capsule axis approximated by three spheres along the body
  for (const h of [0.45, 0.95, 1.45]) {
    if (segPointDist2(b.px, b.py, b.pz, b.x, b.y, b.z, x, y + h, z) <= rr * rr) return true;
  }
  return false;
}

/** Initial velocity for a lob that lands at (tx,ty,tz) after `flight` seconds. */
export function lobVelocity(ox: number, oy: number, oz: number, tx: number, ty: number, tz: number, flight: number, g: number) {
  return {
    vx: (tx - ox) / flight,
    vy: (ty - oy + 0.5 * g * flight * flight) / flight,
    vz: (tz - oz) / flight,
  };
}
